import { Component, inject, signal } from '@angular/core';

import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { REDIRECT_AFTER_LOGIN_KEY } from '@shared/services/auth/authentication.guard';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { AccessLogsService } from '@shared/services/entities/accessLogs.service';
import { PasswordStrengthComponent } from '@shared/security/password-strength.component';
import {
  passwordPolicyValidator,
  passwordMatchValidator,
} from '@shared/security/password-policy.validator';
import {
  emailOrPhoneValidator,
  toCognitoUsername,
  normalizePhone,
} from '@shared/security/phone-alias';

type LoginStep = 'login' | 'newPassword' | 'mfa' | 'emailCode' | 'forgot' | 'forgotConfirm';

/**
 * Pantalla de acceso.
 *
 * Cuando Medtronic era un remote de Module Federation no existía: el shell de
 * Epione autenticaba y dejaba el token en localStorage. Como ahora la app es
 * autónoma, gestiona su propio ciclo de login contra Cognito.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PasswordStrengthComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private usersService = inject(UsersService);
  private rolesService = inject(RolesService);
  private accessLogsService = inject(AccessLogsService);

  step = signal<LoginStep>('login');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isLoading = signal<boolean>(false);
  hidePassword = true;
  hideNewPassword = true;
  hideNewConfirm = true;
  hideForgotPassword = true;
  hideForgotConfirm = true;

  loginForm: FormGroup = this.fb.group({
    // Correo del personal administrativo o teléfono del personal técnico.
    // El teléfono se traduce al alias interno justo antes de llamar a
    // Cognito (ver shared/security/phone-alias.ts).
    username: ['', [Validators.required, emailOrPhoneValidator]],
    password: ['', [Validators.required]],
  });

  /**
   * Primer acceso. Antes pedía `minLength(8)`, pero la política real son 12
   * caracteres con mayúscula, minúscula, número y símbolo
   * (`shared/security/password-policy.ts`): el formulario aceptaba
   * contraseñas que Cognito después rechazaba. Ahora usa el validador de la
   * política y exige confirmación.
   *
   * El contexto (email/nombre) se lee dentro del closure, o sea al validar,
   * para que la política pueda bloquear contraseñas que contengan el correo
   * o el nombre del propio usuario.
   */
  newPasswordForm: FormGroup = this.fb.group(
    {
      name: ['', [Validators.required, Validators.minLength(2)]],
      newPassword: [
        '',
        [
          Validators.required,
          passwordPolicyValidator(() => ({
            email: this.loginForm?.get('username')?.value ?? '',
            name: this.newPasswordForm?.get('name')?.value ?? '',
          })),
        ],
      ],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: passwordMatchValidator('newPassword', 'confirmPassword') }
  );

  codeForm: FormGroup = this.fb.group({
    code: ['', [Validators.required]],
  });

  forgotForm: FormGroup = this.fb.group({
    username: ['', [Validators.required, emailOrPhoneValidator]],
  });

  forgotConfirmForm: FormGroup = this.fb.group(
    {
      code: ['', [Validators.required]],
      newPassword: [
        '',
        [
          Validators.required,
          passwordPolicyValidator(() => ({
            email: this.forgotForm?.get('username')?.value ?? '',
          })),
        ],
      ],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: passwordMatchValidator('newPassword', 'confirmPassword') }
  );

  private clearMessages(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  /**
   * Entra a la app tras autenticar.
   *
   * Si el usuario venía de una ruta profunda (p. ej. Chrome restauró la
   * pestaña en `/schedule` y la sesión ya había expirado), el
   * `AuthenticationGuard` dejó esa URL en sessionStorage; lo natural es
   * devolverlo ahí en vez de mandarlo siempre al tablero.
   */
  /**
   * Bitácora de accesos (Auditoría → Historial de Acceso): un registro por
   * inicio de sesión exitoso. Nunca bloquea la entrada: cualquier error se
   * ignora en silencio.
   */
  private async recordAccess(): Promise<void> {
    try {
      const user = await this.usersService.loadCurrentUser(true);
      if (!user?.id) return;
      let roleName: string | null = null;
      if (user.role) {
        try {
          const response: any = await this.rolesService.search({ searchType: 'default', condition: { id: user.role } });
          roleName = response?.data?.[0]?.name ?? null;
        } catch { /* sin rol legible: se registra sin él */ }
      }
      await this.accessLogsService.create({
        userId: user.id,
        name: user.name ?? null,
        email: user.email ?? null,
        roleName,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        active: true,
      });
    } catch (err) {
      console.warn('[Login] No se pudo registrar el acceso:', err);
    }
  }

  private goToApp(): void {
    void this.recordAccess();
    let target = '/dashboard';
    try {
      const saved = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
      sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
      // Solo rutas internas: nunca una URL absoluta venida de fuera.
      if (saved && saved.startsWith('/') && !saved.startsWith('//')) {
        target = saved;
      }
    } catch {
      // sessionStorage bloqueado: caemos al tablero.
    }
    this.router.navigateByUrl(target);
  }

  async onLogin(): Promise<void> {
    if (this.loginForm.invalid) return;
    this.clearMessages();
    this.isLoading.set(true);
    try {
      const { username, password } = this.loginForm.value;
      const { authState } = await this.authService.logIn(toCognitoUsername(username), password);

      switch (authState) {
        case 'loginSuccess':
          this.goToApp();
          break;
        case 'newPasswordRequired':
          this.step.set('newPassword');
          break;
        case 'mfaRequired':
          this.step.set('mfa');
          break;
        case 'emailCodeRequired':
          this.step.set('emailCode');
          break;
      }
    } catch (err: any) {
      this.errorMessage.set(err?.message ?? 'No se pudo iniciar sesión.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onConfirmNewPassword(): Promise<void> {
    if (this.newPasswordForm.invalid) return;
    this.clearMessages();
    this.isLoading.set(true);
    try {
      const { newPassword, name } = this.newPasswordForm.value;
      const result = await this.authService.confirmPassword(newPassword, name);
      if (result.isSignedIn) {
        await this.syncUserName(name);
        this.goToApp();
      } else {
        this.step.set('login');
      }
    } catch (err: any) {
      this.errorMessage.set(err?.message ?? 'No se pudo confirmar la contraseña.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onSendCode(): Promise<void> {
    if (this.codeForm.invalid) return;
    this.clearMessages();
    this.isLoading.set(true);
    try {
      await this.authService.sendVerificationCode(this.codeForm.value.code);
      this.goToApp();
    } catch (err: any) {
      this.errorMessage.set(err?.message ?? 'Código incorrecto.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onForgotPassword(): Promise<void> {
    if (this.forgotForm.invalid) return;
    this.clearMessages();
    this.isLoading.set(true);
    try {
      const forgotInput = this.forgotForm.value.username;
      await this.authService.forgotPassword(toCognitoUsername(forgotInput));
      this.successMessage.set(
        normalizePhone(forgotInput)
          ? 'Te enviamos un código por SMS a tu teléfono.'
          : 'Te enviamos un código a tu correo.'
      );
      this.step.set('forgotConfirm');
    } catch (err: any) {
      this.errorMessage.set(err?.message ?? 'No se pudo iniciar el reseteo.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onConfirmForgotPassword(): Promise<void> {
    if (this.forgotConfirmForm.invalid) return;
    this.clearMessages();
    this.isLoading.set(true);
    try {
      const { code, newPassword } = this.forgotConfirmForm.value;
      await this.authService.confirmForgotPassword(toCognitoUsername(this.forgotForm.value.username), code, newPassword);
      this.successMessage.set('Contraseña actualizada. Ya puedes iniciar sesión.');
      this.step.set('login');
    } catch (err: any) {
      this.errorMessage.set(err?.message ?? 'No se pudo confirmar la nueva contraseña.');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Propaga a `surgical.users` el nombre que el usuario escribió en el primer
   * acceso.
   *
   * `confirmSignIn` solo lo escribe en Cognito. La fila de la tabla se creó
   * antes (por el alta desde la pantalla de Usuarios, o sembrada a mano), así
   * que sin esto el nombre en Cognito y el de la base quedaban distintos: la
   * app mostraba uno y el token traía otro.
   *
   * Es best-effort: si falla no se bloquea el acceso, porque la sesión ya es
   * válida y el nombre es un dato secundario.
   */
  private async syncUserName(name: string): Promise<void> {
    const nombre = (name ?? '').trim().toUpperCase();
    if (!nombre) return;

    try {
      const user = await this.usersService.loadCurrentUser(true);
      if (!user?.id || user.name === nombre) return;
      await this.usersService.updateUser({ id: user.id, name: nombre });
    } catch (err) {
      console.warn('[Login] No se pudo sincronizar el nombre con la base:', err);
    }
  }

  goTo(step: LoginStep): void {
    this.clearMessages();
    this.step.set(step);
  }
}
